import type { RefObject } from 'react';
import type { DyeDto } from '../api/wardrobe';
import { Dialog } from '../components/BottomSheet';
import { Aside } from '../components/Aside';

export type DyeSheetProps = {
  itemName: string;
  /** 1 (ItemDto.dyeChannels, one-body: `item.schema.json` clamps this to {0, 1}) — the sheet
   *  renders exactly this many swatch rows, one per
   *  channel "1".."dyeChannels" (channel keys are strings — EquippedSlot.dye mirrors the
   *  server's Map<Integer,String>, which always serialises integer keys as strings). */
  dyeChannels: number;
  /** The equipped item's current dye map (channel -> owned dye code), so already-applied
   *  channels render with their selection ring + check on open. */
  dye: Record<string, string>;
  ownedDyes: DyeDto[];
  onSelect: (channel: string, dyeCode: string) => void;
  onClear: (channel: string) => void;
  onClose: () => void;
  /** The "Dye X" button that opened this sheet — see BottomSheet.tsx's own doc comment on why
   *  this is needed for reliable focus-return (this app's sheets are externally controlled, not
   *  built on `Dialog.Trigger`). */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /** Gallery-only — see BottomSheet.tsx's own doc comment. */
  portalContainer?: HTMLElement | null;
};

/**
 * SCREENS.md "dye-sheet-open": an overlay bottom sheet, 1–3 channel swatch rows, live preview
 * updates as swatches toggle (the live-update half of that lives in WardrobeScreen — every tap
 * here calls straight back out to `onSelect`/`onClear`, which the screen wires to
 * `useOutfitDraft.setDye`/`clearDye` + an immediate server commit, same as an equip/unequip).
 * task-15-brief.md: 44 CSS px swatches with name labels (§11.8 "never colour alone" — the
 * selected swatch gets a focus-style ring AND a check glyph, not just a highlighted hue), a
 * "no dye" swatch per row to clear the channel.
 *
 * Built on `Aside` (Task 10, the Studio): below the studio tiers that IS `BottomSheet`
 * (`@radix-ui/react-dialog` — FABLE5_BUILD_PROMPT.md §3), which supplies the focus trap,
 * Escape-to-close, and overlay-click-to-close, with every prop forwarded untouched; on a studio
 * tier the same body renders as a titled panel in the Studio's right slot, REPLACING the item
 * drawer for as long as it is open and handing the slot back on Close — the spec's "fit & dye"
 * panel. This component only adds its own header/body content plus the explicit Close button
 * (`Dialog.Close`; hidden by CSS inside a panel, whose own header carries the close).
 */
export function DyeSheet({
  itemName, dyeChannels, dye, ownedDyes, onSelect, onClear, onClose, restoreFocusRef, portalContainer,
}: DyeSheetProps) {
  const channels = Array.from({ length: Math.max(0, dyeChannels) }, (_, i) => String(i + 1));

  return (
    <Aside
      title={`Dye ${itemName}`}
      open
      onOpenChange={(next) => { if (!next) onClose(); }}
      contentClassName="dye-sheet"
      restoreFocusRef={restoreFocusRef}
      portalContainer={portalContainer}
    >
      <div className="sheet-header">
        <Dialog.Title asChild>
          <h2>Dye {itemName}</h2>
        </Dialog.Title>
        <Dialog.Close asChild>
          <button type="button" className="sheet-close" aria-label="Close dye sheet">
            Close
          </button>
        </Dialog.Close>
      </div>
      <Dialog.Description className="sr-only">
        Choose a dye for each of {itemName}&rsquo;s dye channels, or clear a channel back to no dye.
      </Dialog.Description>

      <div className="sheet-body">
        {channels.map((channel) => {
          const selectedCode = dye[channel];
          return (
            <div key={channel} className="dye-channel-row" role="group" aria-label={`Channel ${channel}`}>
              <span className="dye-channel-label">Channel {channel}</span>
              <div className="dye-swatch-list">
                <button
                  type="button"
                  className={`dye-swatch dye-swatch-none${!selectedCode ? ' dye-swatch-selected' : ''}`}
                  aria-pressed={!selectedCode}
                  onClick={() => onClear(channel)}
                >
                  <span className="dye-swatch-chip dye-swatch-chip-none" aria-hidden="true" />
                  <span className="dye-swatch-name">No dye</span>
                  {!selectedCode && <span className="dye-swatch-check" aria-hidden="true">✓</span>}
                </button>
                {ownedDyes.map((d) => {
                  const selected = selectedCode === d.code;
                  return (
                    <button
                      key={d.code}
                      type="button"
                      className={`dye-swatch${selected ? ' dye-swatch-selected' : ''}`}
                      aria-pressed={selected}
                      onClick={() => onSelect(channel, d.code)}
                    >
                      <span className="dye-swatch-chip" style={{ background: d.hex }} aria-hidden="true" />
                      <span className="dye-swatch-name">{d.name}</span>
                      {selected && <span className="dye-swatch-check" aria-hidden="true">✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Aside>
  );
}
